import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Images, ArrowRight } from "lucide-react";
import { API } from "../lib/api";
import { useSiteContent } from "../lib/siteContent";

const EASE = [0.22, 1, 0.36, 1];

// "Echoes of the Form" teaser: 3 matte-framed plates beneath a frosted boundary,
// inviting readers into the complete Vault gallery. Copy is admin-editable.
export const ArtGalleryTeaser = ({ variant = "home", className = "" }) => {
  const site = useSiteContent();
  const [plates, setPlates] = useState([]);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/gallery`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        if (!alive) return;
        const art = (d.items || []).filter((i) => i.kind === "art").slice(0, 3);
        setPlates(art);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (plates.length === 0) return null;

  const t = site?.teaser || {};
  const eyebrow = t.eyebrow || "Echoes of the Form · A Glimpse of the Archive";
  const body = t.body || "Art is another way the body speaks what language cannot yet reach. The complete curated collection—layered with Casey Muze's audio meditations and somatic context—lives quietly inside The Language Vault.";
  const ctaLabel = t.cta_label || "Enter The Complete Vault Gallery";

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
      data-testid={`art-gallery-teaser-${variant}`}
      className={`relative overflow-hidden rounded-2xl bg-ivory-50 ring-1 ring-ivory-400/60 shadow-[0_24px_60px_-40px_rgba(60,46,30,0.5)] ${className}`}
    >
      {/* Heading + intro, above the plates */}
      <div className="px-6 pb-2 pt-6 text-center">
        <span className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-ivory-50 ring-1 ring-gold/40">
          <Images strokeWidth={1.4} className="h-5 w-5 text-gold-deep" />
        </span>
        <p className="kicker text-gold-foil" data-testid={`art-gallery-teaser-eyebrow-${variant}`}>{eyebrow}</p>
        <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-ink-soft">{body}</p>
      </div>

      {/* Plates — stacked on portrait phones, 3-across on tablet/desktop/landscape; caption under each */}
      <div className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-3 sm:gap-3 landscape:grid-cols-3">
        {plates.map((p, i) => (
          <motion.figure
            key={p.index}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 + i * 0.1, ease: EASE }}
            className="group m-0 flex flex-col"
          >
            <div className="relative aspect-[3/4] overflow-hidden rounded-lg bg-[#E8E2D9] p-1.5 ring-1 ring-ivory-400/70">
              <img
                src={p.image}
                alt={p.title}
                loading="lazy"
                className="h-full w-full rounded-[6px] object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.05]"
              />
            </div>
            <figcaption className="mt-2 px-1 text-center font-display text-[13px] leading-tight text-ink-soft">{p.title}</figcaption>
          </motion.figure>
        ))}
      </div>

      {/* Invitation at the bottom */}
      <div className="px-6 pb-7 pt-1 text-center">
        <Link
          to="/vault/gallery"
          data-testid={`art-gallery-teaser-cta-${variant}`}
          className="debossed-btn inline-flex items-center gap-2 rounded-full px-6 py-2.5 font-display text-sm text-ink transition hover:gap-3"
        >
          {ctaLabel} <ArrowRight strokeWidth={1.6} className="h-4 w-4 text-gold-deep" />
        </Link>
      </div>
    </motion.div>
  );
};
